import { NavLink, Link } from "react-router-dom";
import { useAuth } from "@/auth";
import { useSystem, useServers } from "@/lib/hooks";
import {
  LayoutDashboard, Globe, UploadCloud, TerminalSquare, Settings,
  Server, FlaskConical, LogOut, Rocket, ChevronRight, HardDrive, Zap
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

const navItems = [
  { to: "/", label: "Overview", icon: LayoutDashboard, testid: "nav-overview" },
  { to: "/sites", label: "Sites", icon: Globe, testid: "nav-sites" },
  { to: "/upload", label: "Upload", icon: UploadCloud, testid: "nav-upload" },
  { to: "/servers", label: "Servers", icon: HardDrive, testid: "nav-servers" },
  { to: "/installer", label: "Installer", icon: TerminalSquare, testid: "nav-installer" },
  { to: "/settings", label: "Settings", icon: Settings, testid: "nav-settings" },
];

function isServerReady(s) {
  return !!(s && s.reachable && s.docker && s.docker_compose && s.caddy && s.webnet);
}

export function AppShell({ children }) {
  const { user, logout } = useAuth();
  const { data: sys } = useSystem();
  const { data: servers } = useServers();
  const readyServers = (servers || []).filter(isServerReady);
  const readyCount = readyServers.length;
  const totalServers = (servers || []).length;
  const anyRegistered = totalServers > 0;
  const liveViaVps = readyCount > 0;

  return (
    <div className="min-h-screen relative">
      {liveViaVps ? (
        <div
          data-testid="app-live-mode-banner"
          className="w-full text-sm font-medium"
          style={{ background: "hsl(173 80% 22%)", color: "hsl(166 76% 92%)" }}
        >
          <div className="max-w-[1400px] mx-auto px-4 py-2 flex items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <Zap className="h-4 w-4" />
              <span>
                Live deploy ready — {readyCount} VPS{readyCount === 1 ? "" : "es"} bootstrapped.
                Uploads with a selected deploy target run real Docker on your server(s).
              </span>
            </div>
            <Link to="/servers" className="underline underline-offset-2 hover:opacity-90 shrink-0">
              Manage servers →
            </Link>
          </div>
        </div>
      ) : (
        <div
          data-testid="app-simulation-mode-banner"
          className="w-full text-sm font-medium"
          style={{ background: "hsl(35 92% 28%)", color: "hsl(33 100% 92%)" }}
        >
          <div className="max-w-[1400px] mx-auto px-4 py-2 flex items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <FlaskConical className="h-4 w-4" />
              <span>
                {anyRegistered
                  ? "Server registered but not fully ready — bootstrap it to enable real deploys."
                  : "No VPS registered yet — deploys will run in simulation. Add a server for real deploys."}
              </span>
            </div>
            <Link to="/servers" className="underline underline-offset-2 hover:opacity-90 shrink-0">
              {anyRegistered ? "Open Servers →" : "Add server →"}
            </Link>
          </div>
        </div>
      )}

      <div className="emhost-shell">
        {/* Sidebar */}
        <aside className="surface-1 border-r border-border hidden md:flex md:flex-col">
          <div className="px-5 py-5 flex items-center gap-2">
            <div className="h-8 w-8 rounded-lg grid place-items-center"
                 style={{ background: "hsl(188 86% 45%)" }}>
              <Rocket className="h-4 w-4" style={{ color: "hsl(222 47% 7%)" }} />
            </div>
            <div className="flex-1">
              <div className="font-semibold tracking-tight">emhost</div>
              <div className="text-[11px] text-muted-foreground">Ship any ZIP. Any stack.</div>
            </div>
          </div>

          <div className="px-3">
            <div data-testid="mode-indicator-badge">
              <Badge
                variant="outline"
                className="w-full justify-between gap-2 py-1.5"
                style={{
                  background: liveViaVps ? "hsl(173 80% 22%)" : "hsl(35 92% 22%)",
                  color: liveViaVps ? "hsl(166 76% 92%)" : "hsl(33 100% 92%)",
                  borderColor: "transparent",
                }}
              >
                <span className="flex items-center gap-2">
                  {liveViaVps ? <Zap className="h-3.5 w-3.5" /> : <FlaskConical className="h-3.5 w-3.5" />}
                  {liveViaVps
                    ? `Live via ${readyCount} VPS${readyCount === 1 ? "" : "es"}`
                    : "Simulation mode"}
                </span>
                <ChevronRight className="h-3.5 w-3.5 opacity-70" />
              </Badge>
            </div>
          </div>

          <nav className="px-2 py-3 flex-1 space-y-0.5">
            {navItems.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.to === "/"}
                data-testid={n.testid}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors ${
                    isActive
                      ? "bg-[hsl(var(--surface-2))] text-foreground"
                      : "text-muted-foreground hover:text-foreground hover:bg-[hsl(var(--surface-2))]/50"
                  }`
                }
              >
                <n.icon className="h-[18px] w-[18px]" strokeWidth={1.75} />
                <span>{n.label}</span>
              </NavLink>
            ))}
          </nav>

          <div className="px-3 py-3 border-t border-border">
            <div className="flex items-center gap-2 text-sm">
              <div className="h-8 w-8 rounded-full surface-2 grid place-items-center text-xs">
                {(user?.email || "a").charAt(0).toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <div className="truncate">{user?.email || "admin"}</div>
                <div className="text-[11px] text-muted-foreground">signed in</div>
              </div>
              <Button data-testid="nav-logout-button" variant="ghost" size="icon" onClick={logout}>
                <LogOut className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </aside>

        {/* Main */}
        <main className="min-w-0 bg-background">
          <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-6 md:py-8">
            {children}
          </div>
        </main>
      </div>
    </div>
  );
}
